import React, { useState } from 'react';
import { FileDown } from 'lucide-react';
import { Modal } from './ui/Modal';
import { MAX_COMBINED_DOCUMENTS } from './documentFolderPdf';

/**
 * Options for "Export folder as PDF".
 *
 * WHY A MODAL AT ALL, given the export worked without one: this is a slow,
 * one-shot action. Reading N documents takes seconds, the result goes straight
 * into a save dialog, and getting the contents page wrong means doing the whole
 * thing again. A prompt in front of a cheap, instantly-undoable action would be
 * a tax; in front of this one it is the cheaper moment to decide.
 *
 * IT ALSO ANSWERS A QUESTION THE OLD FLOW ANSWERED TOO LATE. The export caps at
 * MAX_COMBINED_DOCUMENTS and used to report the shortfall AFTER the user had
 * waited and picked a filename. The count is known up front — `collectFolderSubtree`
 * is pure and needs no I/O — so the warning belongs here, before the wait.
 *
 * Deliberately only the two options that were asked for. "Start each document
 * on a new page" and "include subfolder headings" are structural choices where
 * one answer is right (see documentFolderPdf.js); offering them would be
 * decoration that invites a worse PDF.
 */
export default function DocumentFolderPdfModal({
  isOpen,
  folderName = 'Documents',
  documentCount = 0,
  onCancel,
  onConfirm,
}) {
  const [includeToc, setIncludeToc] = useState(true);
  const [includePageNumbers, setIncludePageNumbers] = useState(true);

  const included = Math.min(documentCount, MAX_COMBINED_DOCUMENTS);
  const skipped = Math.max(0, documentCount - MAX_COMBINED_DOCUMENTS);
  // A contents page over one document is a page with one line on it. The
  // builder already refuses; saying so here beats a checkbox that silently
  // does nothing.
  const tocMeaningful = included > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="Export folder as PDF"
      subtitle={folderName}
      customIcon={<FileDown size={24} className="text-blue-500" />}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm({ includeToc: includeToc && tocMeaningful, includePageNumbers });
        }}
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {included === 1
            ? '1 document will be combined into a single PDF.'
            : `${included} documents will be combined into a single PDF, in the order they appear in the sidebar.`}
        </p>

        {skipped > 0 && (
          /* Said BEFORE the wait, not after the save dialog. */
          <p className="text-sm text-amber-600 dark:text-amber-500">
            {skipped} more {skipped === 1 ? 'document is' : 'documents are'} in this folder than a
            single PDF holds, so {skipped === 1 ? 'it' : 'they'} will be left out. Export the
            subfolders separately to include {skipped === 1 ? 'it' : 'them'}.
          </p>
        )}

        <div className="flex items-start space-x-2">
          <input
            type="checkbox"
            id="folder_pdf_toc"
            checked={includeToc && tocMeaningful}
            disabled={!tocMeaningful}
            onChange={(e) => setIncludeToc(e.target.checked)}
            className="checkbox-brand cursor-pointer mt-0.5"
          />
          <label htmlFor="folder_pdf_toc" className="cursor-pointer text-sm">
            {/* A <div>, not <span className="block">: this build emits no
                `.block` utility at all (only Monaco's .blockDecorations-*), so
                that class was doing nothing and the description ran on the same
                line as its label. Several older files make the same assumption;
                using an element that is block BY NATURE cannot regress if the
                utility never arrives. */}
            <div className="text-gray-900 dark:text-zinc-200">Contents page</div>
            <div className="text-xs text-gray-500 dark:text-zinc-400">
              {tocMeaningful
                ? 'Lists every document with its real page number, on its own page at the front.'
                : 'Not available for a single document.'}
            </div>
          </label>
        </div>

        <div className="flex items-start space-x-2">
          <input
            type="checkbox"
            id="folder_pdf_page_numbers"
            checked={includePageNumbers}
            onChange={(e) => setIncludePageNumbers(e.target.checked)}
            className="checkbox-brand cursor-pointer mt-0.5"
          />
          <label htmlFor="folder_pdf_page_numbers" className="cursor-pointer text-sm">
            <div className="text-gray-900 dark:text-zinc-200">Page numbers</div>
            <div className="text-xs text-gray-500 dark:text-zinc-400">
              &ldquo;3 / 12&rdquo; centred in the footer of every page.
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="modern-button-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="modern-button">
            Export
          </button>
        </div>
      </form>
    </Modal>
  );
}
